/**
 * The list arithmetic behind "My sayings": move-to-index, restore-default-order, and the two
 * small readings a pointer drag and a typed position need.
 *
 * Pure and DOM-free, beside the component rather than inside its JSX — the rule
 * `game/hud/saying-lines.ts` and `game/input/actions.ts` are already under, for their reason: a
 * reorder is a *rule* ("a number outside the list is clamped to it", "restoring the order does not
 * restore what you removed") and a rule is a thing to assert, not a thing to read out of a render.
 *
 * Nothing here widens anybody's vocabulary. Every function takes ids that are already on the draft
 * and answers a permutation of exactly those — `sayings.updateList` refuses a saying outside the
 * catalogue plus this account's approved submissions, and there is no path from this module to a
 * list that contains one more id than it was given.
 */

import { DEFAULT_SAYINGS } from '@chompdome/shared/constants';

/**
 * Move the entry at `from` to `to`, clamped to the list.
 *
 * The same primitive serves all three controls — an arrow press is `to = from ± 1`, a typed
 * position is `to = what they typed - 1`, and a drag is `to = whichever row the pointer is over` —
 * which is what keeps "moved" meaning one thing on this screen. Out-of-range and no-op moves
 * answer the list they were given, by reference, so a `setState` on one costs no render.
 */
export function moveSayingTo(list: readonly string[], from: number, to: number): readonly string[] {
  if (from < 0 || from >= list.length) return list;
  const target = Math.min(Math.max(to, 0), list.length - 1);
  if (target === from) return list;
  const next = [...list];
  const [row] = next.splice(from, 1);
  next.splice(target, 0, row);
  return next;
}

/**
 * The draft re-sorted into the catalogue's own order: the defaults first in `DEFAULT_SAYINGS`
 * order, then this account's approved customs in `customOrder`.
 *
 * **It reorders what is on the list and re-adds nothing.** "Restore default order" is a statement
 * about order, and a player who deliberately took "No u." off their picker did not ask for it back
 * — putting it back would make this button the one control on the card whose name understates what
 * it does, and the way to undo a removal is already on screen ("Not on your list" puts it back in
 * one click). The list this returns is a permutation of the one it was given, always.
 *
 * An id in neither set — a custom whose approval was since withdrawn, say — sorts to the end
 * keeping its relative order, rather than being dropped: this function may not lose a row.
 */
export function restoreDefaultSayingOrder(
  list: readonly string[],
  customOrder: readonly string[],
): readonly string[] {
  const rank = new Map<string, number>();
  DEFAULT_SAYINGS.forEach((saying, index) => rank.set(saying.id, index));
  customOrder.forEach((id, index) => {
    if (!rank.has(id)) rank.set(id, DEFAULT_SAYINGS.length + index);
  });
  const unknown = DEFAULT_SAYINGS.length + customOrder.length;
  const ranked = list.map((id, index) => ({ id, rank: rank.get(id) ?? unknown + index }));
  ranked.sort((a, b) => a.rank - b.rank);
  const next = ranked.map((row) => row.id);
  return next.every((id, index) => id === list[index]) ? list : next;
}

/**
 * The index a typed position asks for, or null for anything that is not one.
 *
 * The field is 1-based because it is a place in a list a person is reading, and it is *clamped*
 * rather than refused: somebody typing 99 into a list of twelve means the bottom, and a red border
 * would be the card arguing with them about arithmetic. Only text that is not a whole number at
 * all is nothing — an empty field is a player who selected the digits and has not typed yet.
 */
export function parseSayingPosition(raw: string, total: number): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const asked = Number.parseInt(trimmed, 10);
  return Math.min(Math.max(asked, 1), Math.max(total, 1)) - 1;
}

/** One row's vertical extent, in the same client coordinates a pointer event reports. */
export interface RowSpan {
  top: number;
  height: number;
}

/**
 * Which row a pointer at `pointerY` is over, clamped to the ends of the list.
 *
 * The whole of the drag's geometry, kept here so it can be asserted against numbers rather than
 * against a layout jsdom does not have. Above the first row is the first row and below the last is
 * the last, because a drag that leaves the list at the top is unambiguously "put it at the top" —
 * the drag is a live reorder, so this is asked on every move and answers where the row goes *now*.
 */
export function dropIndexAt(spans: readonly RowSpan[], pointerY: number): number {
  if (spans.length === 0) return 0;
  for (let index = 0; index < spans.length; index += 1) {
    const span = spans[index];
    if (pointerY < span.top + span.height) return index;
  }
  return spans.length - 1;
}
